import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { useSettings, type Settings } from '@/contexts/SettingsContext';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from '@/lib/toast';
import { Settings2, Sparkles, Download, Shield, Wrench, Target } from 'lucide-react';
import { EXTENSION_WEB_STORE_URL, APP_VERSION } from '@/constants';

type TabId = 'general' | 'ai' | 'ats' | 'export' | 'privacy' | 'advanced';

interface TabDef {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

export function SettingsModal() {
  const { settings, update, reset, isOpen, closeSettings } = useSettings();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<TabId>('general');

  const tabs: TabDef[] = [
    { id: 'general', label: t('settings.tabs.general'), icon: <Settings2 size={16} aria-hidden="true" /> },
    { id: 'ai', label: t('settings.tabs.ai'), icon: <Sparkles size={16} aria-hidden="true" /> },
    { id: 'ats', label: t('settings.tabs.ats'), icon: <Target size={16} aria-hidden="true" /> },
    { id: 'export', label: t('settings.tabs.export'), icon: <Download size={16} aria-hidden="true" /> },
    { id: 'privacy', label: t('settings.tabs.privacy'), icon: <Shield size={16} aria-hidden="true" /> },
    { id: 'advanced', label: t('settings.tabs.advanced'), icon: <Wrench size={16} aria-hidden="true" /> },
  ];

  return (
    <Modal open={isOpen} onClose={closeSettings} size="lg" title={t('settings.title')} description={t('settings.description')}>
      <div className="flex flex-col md:flex-row gap-4 md:min-h-[400px]">
        {/* Tab nav — horizontal scroll on mobile, sidebar on desktop */}
        <nav className="md:w-44 shrink-0 flex md:flex-col gap-1 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0 border-b md:border-b-0 border-white/10 scrollbar-hide" aria-label={t('settings.title')}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors whitespace-nowrap shrink-0 ${
                activeTab === tab.id
                  ? 'bg-violet-500/15 text-violet-300 border border-violet-500/30'
                  : 'text-white/70 hover:bg-white/5 hover:text-white border border-transparent'
              }`}
              aria-current={activeTab === tab.id ? 'page' : undefined}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'general' && <GeneralTab settings={settings} update={update} t={t} />}
          {activeTab === 'ai' && <AiTab settings={settings} update={update} t={t} />}
          {activeTab === 'ats' && <AtsTab settings={settings} update={update} t={t} />}
          {activeTab === 'export' && <ExportTab settings={settings} update={update} t={t} />}
          {activeTab === 'privacy' && <PrivacyTab settings={settings} update={update} t={t} />}
          {activeTab === 'advanced' && <AdvancedTab reset={reset} t={t} />}
        </div>
      </div>
    </Modal>
  );
}

// Reusable row component
function Row({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-white/5 last:border-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white/90">{label}</div>
        {description && <div className="text-xs text-white/50 mt-0.5">{description}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-md bg-white/5 p-0.5 border border-white/10">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 text-xs rounded transition-colors ${
            value === opt.value ? 'bg-violet-500/25 text-violet-200' : 'text-white/60 hover:text-white'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-violet-500' : 'bg-white/10'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  );
}

interface TabProps {
  settings: Settings;
  update: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  t: (k: string) => string;
}

function GeneralTab({ settings, update, t }: TabProps) {
  return (
    <div>
      <Row label={t('settings.general.mode')} description={t('settings.general.modeDesc')}>
        <Segmented
          value={settings.mode}
          options={[
            { value: 'simple', label: t('settings.general.simple') },
            { value: 'pro', label: t('settings.general.pro') },
          ]}
          onChange={(v) => update('mode', v)}
        />
      </Row>
      <Row label={t('settings.general.theme')} description={t('settings.general.themeDesc')}>
        <Segmented
          value={settings.theme}
          options={[
            { value: 'dark', label: '🌙' },
            { value: 'light', label: '☀️' },
            { value: 'auto', label: 'Auto' },
          ]}
          onChange={(v) => update('theme', v)}
        />
      </Row>
      <Row label={t('settings.general.language')} description={t('settings.general.languageDesc')}>
        <Segmented
          value={settings.locale}
          options={[
            { value: 'fr', label: 'FR' },
            { value: 'en', label: 'EN' },
          ]}
          onChange={(v) => update('locale', v)}
        />
      </Row>
    </div>
  );
}

function AiTab({ settings, update, t }: TabProps) {
  return (
    <div>
      <Row label={t('settings.ai.provider')} description={t('settings.ai.providerDesc')}>
        <Segmented
          value={settings.aiPreference}
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 'minimax', label: 'MiniMax' },
            { value: 'openrouter', label: 'OpenRouter' },
          ]}
          onChange={(v) => update('aiPreference', v)}
        />
      </Row>
      <Row label={t('settings.ai.tone')} description={t('settings.ai.toneDesc')}>
        <Segmented
          value={settings.aiTone}
          options={[
            { value: 'formel', label: t('settings.ai.toneFormel') },
            { value: 'neutre', label: t('settings.ai.toneNeutre') },
            { value: 'dynamique', label: t('settings.ai.toneDynamique') },
          ]}
          onChange={(v) => update('aiTone', v)}
        />
      </Row>
      <Row label={t('settings.ai.length')} description={t('settings.ai.lengthDesc')}>
        <Segmented
          value={settings.aiLength}
          options={[
            { value: 'court', label: t('settings.ai.lengthCourt') },
            { value: 'standard', label: t('settings.ai.lengthStandard') },
            { value: 'detaille', label: t('settings.ai.lengthDetaille') },
          ]}
          onChange={(v) => update('aiLength', v)}
        />
      </Row>

      {/* Separator */}
      <div className="pt-2 pb-1">
        <p className="text-[10px] text-white/30 uppercase tracking-wider font-bold">{t('settings.ai.coverLetterSection')}</p>
      </div>

      <Row label={t('settings.ai.coverLetterLength')} description={t('settings.ai.coverLetterLengthDesc')}>
        <Segmented
          value={settings.coverLetterLength}
          options={[
            { value: 'flash', label: t('settings.ai.coverLetterFlash') },
            { value: 'short', label: t('settings.ai.coverLetterShort') },
            { value: 'standard', label: t('settings.ai.lengthStandard') },
            { value: 'long', label: t('settings.ai.coverLetterLong') },
          ]}
          onChange={(v) => update('coverLetterLength', v)}
        />
      </Row>
      <Row label={t('settings.ai.coverLetterTone')} description={t('settings.ai.coverLetterToneDesc')}>
        <Segmented
          value={settings.coverLetterTone}
          options={[
            { value: 'direct', label: t('settings.ai.coverLetterToneDirect') },
            { value: 'professionnel', label: t('settings.ai.coverLetterTonePro') },
            { value: 'dynamique', label: t('settings.ai.coverLetterToneDyn') },
            { value: 'enthousiaste', label: t('settings.ai.coverLetterToneEnth') },
          ]}
          onChange={(v) => update('coverLetterTone', v)}
        />
      </Row>
      <Row label={t('settings.ai.coverLetterFidelity')} description={t('settings.ai.coverLetterFidelityDesc')}>
        <Segmented
          value={settings.coverLetterFidelity}
          options={[
            { value: 'fidele', label: t('settings.ai.coverLetterFidFidele') },
            { value: 'modere', label: t('settings.ai.coverLetterFidEquil') },
            { value: 'creatif', label: t('settings.ai.coverLetterFidCreatif') },
          ]}
          onChange={(v) => update('coverLetterFidelity', v)}
        />
      </Row>
    </div>
  );
}

function AtsTab({ settings, update, t }: TabProps) {
  return (
    <div>
      <p className="text-xs text-white/40 mb-4 leading-relaxed">
        {t('settings.ats.presetNote')}
      </p>

      <Row
        label={t('settings.ats.presetLabel')}
        description={t('settings.ats.presetDesc')}
      >
        <Segmented
          value={settings.defaultAtsPreset}
          options={[
            { value: 'conservateur', label: t('settings.ats.presetConservateur') },
            { value: 'standard', label: t('settings.ats.presetStandard') },
            { value: 'inventif', label: t('settings.ats.presetInventif') },
          ]}
          onChange={(v) => update('defaultAtsPreset', v)}
        />
      </Row>

      <div className="mt-4 p-3 rounded-xl bg-white/5 border border-white/10 space-y-2.5">
        <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider">{t('settings.ats.presetDetailsTitle')}</p>
        <div className="space-y-2">
          <div>
            <p className="text-xs text-white/80 font-medium">{t('settings.ats.presetConservateurTitle')}</p>
            <p className="text-xs text-white/50">{t('settings.ats.presetConservateurDesc')}</p>
          </div>
          <div>
            <p className="text-xs text-white/80 font-medium">{t('settings.ats.presetStandardTitle')}</p>
            <p className="text-xs text-white/50">{t('settings.ats.presetStandardDesc')}</p>
          </div>
          <div>
            <p className="text-xs text-white/80 font-medium">{t('settings.ats.presetInventifTitle')}</p>
            <p className="text-xs text-white/50">{t('settings.ats.presetInventifDesc')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExportTab({ settings, update, t }: TabProps) {
  return (
    <div>
      <Row label={t('settings.export.defaultFormat')} description={t('settings.export.defaultFormatDesc')}>
        <Segmented
          value={settings.defaultExportFormat}
          options={[
            { value: 'pdf', label: 'PDF' },
            { value: 'docx', label: 'DOCX' },
            { value: 'typst', label: 'Typst' },
          ]}
          onChange={(v) => update('defaultExportFormat', v)}
        />
      </Row>
      <Row label={t('settings.export.defaultLayout')} description={t('settings.export.defaultLayoutDesc')}>
        <select
          value={settings.defaultLayout}
          onChange={(e) => update('defaultLayout', e.target.value as Settings['defaultLayout'])}
          className="bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-sm text-white min-h-[32px]"
        >
          <option value="classique">{t('visuel.classic')}</option>
          <option value="moderne">{t('visuel.modern')}</option>
          <option value="ats">ATS</option>
          <option value="pro">Pro</option>
        </select>
      </Row>
    </div>
  );
}

function safeReadKeys(keys: string[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    try {
      result[key] = localStorage.getItem(key);
    } catch {
      /* ignore */
    }
  }
  return result;
}

function PrivacyTab({ settings, update, t }: TabProps) {
  const handleExportData = () => {
    const data = {
      settings,
      cvState: safeReadKeys(['cv_all_templates_data', 'cv_versions', 'cv_fp']),
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `atsmycv-export-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('settings.privacy.exportSuccess'));
  };
  return (
    <div>
      <Row label={t('settings.privacy.analytics')} description={t('settings.privacy.analyticsDesc')}>
        <Toggle
          checked={settings.analyticsOptIn}
          onChange={(v) => update('analyticsOptIn', v)}
          ariaLabel={t('settings.privacy.analytics')}
        />
      </Row>
      <Row label={t('settings.privacy.exportData')} description={t('settings.privacy.exportDataDesc')}>
        <button
          type="button"
          onClick={handleExportData}
          className="px-3 py-1.5 text-xs rounded-md bg-white/5 border border-white/10 hover:bg-white/10 text-white/80"
        >
          {t('settings.privacy.exportDataBtn')}
        </button>
      </Row>
      <Row label={t('settings.privacy.policy')} description={t('settings.privacy.policyDesc')}>
        <a
          href="/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 text-xs rounded-md bg-white/5 border border-white/10 hover:bg-white/10 text-white/80 inline-flex items-center gap-1.5"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          {t('settings.privacy.policyBtn')}
        </a>
      </Row>
    </div>
  );
}

interface AdvancedTabProps {
  reset: () => void;
  t: (key: string) => string;
}

function AdvancedTab({ reset, t }: AdvancedTabProps) {
  const handleReset = () => {
    if (window.confirm(t('settings.advanced.resetConfirm'))) {
      reset();
      toast.success(t('settings.advanced.resetSuccess'));
    }
  };

  return (
    <div>
      {/* Extension — fiche Chrome Web Store */}
      <Row label={t('settings.advanced.extensionDownload')} description={t('settings.advanced.extensionDownloadDesc')}>
        <a
          href={EXTENSION_WEB_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-violet-600 hover:bg-violet-500 text-white transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          {t('settings.advanced.extensionDownloadBtn')}
        </a>
      </Row>
      <Row label={t('settings.advanced.reset')} description={t('settings.advanced.resetDesc')}>
        <button
          type="button"
          onClick={handleReset}
          className="px-3 py-1.5 text-xs rounded-md bg-red-500/10 border border-red-500/30 text-red-200 hover:bg-red-500/20"
        >
          {t('settings.advanced.resetBtn')}
        </button>
      </Row>
      <Row label={t('settings.advanced.version')} description={`v${APP_VERSION}`}>
        <span className="text-xs text-white/40">—</span>
      </Row>
    </div>
  );
}
